import React from "react";
import { Helmet } from "react-helmet-async";

export const SITE = "https://camcpc.co.uk";
const DEFAULT_IMAGE = "https://images.unsplash.com/photo-1759262151080-e05ba1c6294f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
// Google Search Console verification token — set REACT_APP_GSC_VERIFICATION in /app/frontend/.env
// (the raw content= value that GSC gives you when you pick the "HTML tag" verification method).
const GSC_VERIFICATION = process.env.REACT_APP_GSC_VERIFICATION || "";

export default function SEO({
  title,
  description,
  path = "",
  image = DEFAULT_IMAGE,
  type = "website",
  jsonLd,
}) {
  const fullTitle = title
    ? `${title} — Cambridge Cardiovascular Prevention Clinic`
    : "Cambridge Cardiovascular Prevention Clinic — Blood Pressure, Heart & Metabolic Health Specialists, Cambridge";
  const desc = description ||
    "Personalised cardiovascular prevention — a specialist clinic led by consultants of Cambridge University Hospitals.";
  const url = `${SITE}${path}`;
  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={url} />
      {GSC_VERIFICATION && (
        <meta name="google-site-verification" content={GSC_VERIFICATION} />
      )}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={image} />
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
}

// Reviews are intentionally empty until the clinic's real Google reviews
// are connected via GOOGLE_PLACES_API_KEY + GOOGLE_PLACE_ID on the backend.
export const REVIEWS = [];
export const AGGREGATE_RATING = null;

// Comprehensive LocalBusiness / MedicalBusiness schema for Home + Google Business Profile
export const ORG_JSON_LD = {
  "@context": "https://schema.org",
  "@type": ["MedicalBusiness", "MedicalClinic", "LocalBusiness"],
  "@id": `${SITE}/#clinic`,
  name: "Cambridge Cardiovascular Prevention Clinic",
  alternateName: ["CCPC", "Cambridge Cardiovascular Prevention Clinic"],
  legalName: "Cambridge Cardiovascular Clinic Ltd",
  url: SITE,
  logo: `${SITE}/brand/logo-mark.png`,
  image: [`${SITE}/brand/logo-full.png`, DEFAULT_IMAGE],
  slogan: "Personalised Cardiovascular Prevention",
  description:
    "Cambridge Cardiovascular Prevention Clinic — a specialist hypertension and cardiometabolic prevention clinic, consultant-led and affiliated with Cambridge University Hospitals.",
  medicalSpecialty: ["Cardiovascular", "PreventiveMedicine", "Endocrine"],
  knowsAbout: ["Hypertension", "High blood pressure", "General internal medicine", "Echocardiography", "ECG",
    "Holter monitoring", "Cholesterol", "Diabetes", "Weight loss", "Heart health check", "Palpitations",
    "Dizziness", "Shortness of breath", "Chest pain", "Tiredness", "Cardiovascular prevention"],
  availableService: [
    "Hypertension and blood pressure (BP) consultation", "24-hour blood pressure monitoring",
    "Echocardiogram (echo heart scan)", "ECG and Holter monitoring", "Cholesterol and lipid assessment",
    "Diabetes and metabolic health assessment", "Medical weight loss programme", "Heart health check",
    "Body composition (bioimpedance)", "Carotid artery scan (cIMT)", "Arterial stiffness", "Flow-mediated dilatation",
  ].map((name) => ({ "@type": "MedicalProcedure", name })),
  address: {
    "@type": "PostalAddress",
    streetAddress: "ACCI Building, Cambridge University Hospitals, Hills Road",
    addressLocality: "Cambridge",
    addressRegion: "Cambridgeshire",
    postalCode: "CB2 0QQ",
    addressCountry: "GB",
  },
  geo: { "@type": "GeoCoordinates", latitude: 52.177, longitude: 0.1402 },
  hasMap:
    "https://www.google.com/maps/place/Cambridge+University+Hospitals/@52.177,0.1402,17z",
  telephone: "+44-1223-256653",
  email: "enquiries@camcpc.co.uk",
  priceRange: "£££",
  currenciesAccepted: "GBP",
  paymentAccepted: "Self-pay",
  areaServed: [
    { "@type": "City", name: "Cambridge" },
    { "@type": "AdministrativeArea", name: "Cambridgeshire" },
    { "@type": "AdministrativeArea", name: "East of England" },
  ],
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      opens: "08:30",
      closes: "18:00",
    },
  ],
  sameAs: [
    "https://www.linkedin.com/company/cambridge-cardiovascular-prevention-clinic",
    ...(process.env.REACT_APP_GBP_URL ? [process.env.REACT_APP_GBP_URL] : []),
  ],
  potentialAction: {
    "@type": "ReserveAction",
    target: `${SITE}/enquiry`,
    name: "Request an appointment",
  },
  hasCredential: [
    {
      "@type": "EducationalOccupationalCredential",
      credentialCategory: "Accreditation",
      name: "ESH-accredited Hypertension Specialist Centre",
      recognizedBy: {
        "@type": "Organization",
        name: "European Society of Hypertension",
        url: "https://www.eshonline.org/",
      },
    },
    {
      "@type": "EducationalOccupationalCredential",
      credentialCategory: "Accreditation",
      name: "EACVI-accredited Echocardiography Centre",
      recognizedBy: {
        "@type": "Organization",
        name: "European Association of Cardiovascular Imaging",
        url: "https://www.escardio.org/Sub-specialty-communities/European-Association-of-Cardiovascular-Imaging-(EACVI)",
      },
    },
  ],
  physician: [
    {
      "@type": "Physician",
      "@id": `${SITE}/consultants/prof-ian-wilkinson#physician`,
      name: "Professor Ian Wilkinson",
      url: `${SITE}/consultants/prof-ian-wilkinson`,
      image: `${SITE}/consultants/ian-wilkinson.jpg`,
      jobTitle: "Professor of Therapeutics · Hypertension specialist",
      medicalSpecialty: ["Cardiovascular", "InternalMedicine"],
      worksFor: { "@id": `${SITE}/#clinic` },
      knowsAbout: [
        "Resistant hypertension",
        "Young-onset hypertension",
        "Hypertension in pregnancy",
        "Secondary hypertension",
      ],
    },
    {
      "@type": "Physician",
      "@id": `${SITE}/consultants/dr-joseph-cheriyan#physician`,
      name: "Dr Joseph Cheriyan",
      url: `${SITE}/consultants/dr-joseph-cheriyan`,
      image: `${SITE}/consultants/joseph-cheriyan.jpg`,
      jobTitle: "Affiliated Associate Professor · Hypertension, cholesterol & cardiovascular risk",
      medicalSpecialty: ["Cardiovascular", "InternalMedicine"],
      worksFor: { "@id": `${SITE}/#clinic` },
      knowsAbout: [
        "Hypertension",
        "Cholesterol and lipid disorders",
        "Cardiovascular risk assessment",
        "Heart failure",
      ],
    },
    {
      "@type": "Physician",
      "@id": `${SITE}/consultants/dr-johann-graggaber#physician`,
      name: "Dr Johann Graggaber",
      url: `${SITE}/consultants/dr-johann-graggaber`,
      image: `${SITE}/consultants/johann-graggaber.jpg`,
      jobTitle: "Affiliated Assistant Professor · Hypertension, hormones & metabolic health",
      medicalSpecialty: ["Cardiovascular", "Endocrine", "InternalMedicine"],
      worksFor: { "@id": `${SITE}/#clinic` },
      knowsAbout: [
        "Hypertension",
        "Endocrine causes of hypertension",
        "Diabetes and metabolic health",
        "General internal medicine",
      ],
    },
    {
      "@type": "Physician",
      "@id": `${SITE}/consultants/navazh-jalaludeen#physician`,
      name: "Navazh Jalaludeen",
      url: `${SITE}/consultants/navazh-jalaludeen`,
      image: `${SITE}/consultants/navazh-jalaludeen.jpg`,
      jobTitle: "Consultant Physiologist in Echocardiography",
      medicalSpecialty: ["Cardiovascular"],
      worksFor: { "@id": `${SITE}/#clinic` },
      knowsAbout: [
        "Transthoracic echocardiography",
        "Heart failure assessment",
        "Valvular heart disease assessment",
      ],
    },
  ],
  availableService: [
    { "@type": "MedicalProcedure", name: "Hypertension consultation and management", url: `${SITE}/services/hypertension-management` },
    { "@type": "MedicalProcedure", name: "Cardiovascular risk assessment", url: `${SITE}/services/cardiovascular-risk-assessment` },
    { "@type": "MedicalProcedure", name: "Cholesterol and lipid management", url: `${SITE}/services/lipid-management` },
    { "@type": "MedicalProcedure", name: "Ambulatory blood-pressure monitoring", url: `${SITE}/services/ambulatory-bp-monitoring` },
    { "@type": "MedicalProcedure", name: "Holter monitoring", url: `${SITE}/services/holter-monitoring` },
    { "@type": "MedicalProcedure", name: "Echocardiography", url: `${SITE}/services/echocardiography` },
    { "@type": "MedicalProcedure", name: "Cardiometabolic medicine", url: `${SITE}/services/cardiometabolic-prevention` },
    { "@type": "MedicalProcedure", name: "Carotid intima-media thickness (cIMT)", url: `${SITE}/services/carotid-imt` },
    { "@type": "MedicalProcedure", name: "Arterial stiffness measurement", url: `${SITE}/services/arterial-stiffness` },
    { "@type": "MedicalProcedure", name: "Flow-mediated dilatation (FMD)", url: `${SITE}/services/flow-mediated-dilatation` },
    { "@type": "MedicalProcedure", name: "Supported weight loss programme", url: `${SITE}/services/supported-weight-loss` },
  ],
};

// Reusable VideoObject JSON-LD for patient-education embeds
export function videoJsonLd({ name, description, thumbnail, uploadDate, url }) {
  return {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    name,
    description,
    thumbnailUrl: thumbnail || `${SITE}/brand/logo-full.png`,
    uploadDate: uploadDate || "2026-02-01",
    embedUrl: url,
    publisher: { "@id": `${SITE}/#clinic` },
    inLanguage: "en-GB",
  };
}

// Reusable BreadcrumbList JSON-LD
// items: [{ name: 'Services', path: '/services' }, { name: 'Hypertension', path: '/services/hypertension-management' }]
export function breadcrumbJsonLd(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
      ...items.map((it, i) => ({
        "@type": "ListItem",
        position: i + 2,
        name: it.name,
        item: `${SITE}${it.path}`,
      })),
    ],
  };
}

// Reusable Article JSON-LD for the Journal
export function articleJsonLd({ headline, description, url, datePublished, image, sourceName, sourceUrl }) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline,
    description,
    url: url || `${SITE}/blog`,
    datePublished,
    image: image || `${SITE}/brand/logo-full.png`,
    inLanguage: "en-GB",
    author: { "@id": `${SITE}/#clinic` },
    publisher: { "@id": `${SITE}/#clinic` },
    mainEntityOfPage: { "@type": "WebPage", "@id": url || `${SITE}/blog` },
    ...(sourceName && sourceUrl
      ? {
          isBasedOn: { "@type": "CreativeWork", name: sourceName, url: sourceUrl },
          citation: sourceUrl,
        }
      : {}),
  };
}
